import React, { useState } from 'react'
import { Box, Tooltip, Typography, makeStyles, useTheme } from '@material-ui/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const useStyles = makeStyles((theme) => ({
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '2px 8px',
    borderRadius: 6,
    backgroundColor: theme.palette.background.sectionOddBg,
    border: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    transition: 'background-color 0.15s, border-color 0.15s',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      borderColor: theme.palette.primary.light
    }
  },
  pillCopied: {
    backgroundColor: theme.palette.success.light,
    borderColor: theme.palette.success.main
  },
  text: {
    fontFamily: 'monospace',
    fontSize: 12,
    letterSpacing: '0.03em',
    color: theme.palette.text.secondary
  },
  icon: {
    fontSize: 10,
    flexShrink: 0
  }
}))

/**
 * TruncCell — universal truncated text cell with copy-to-clipboard
 *
 * Renders any long string (UUIDs, IDs, file paths, etc.) in a compact pill.
 * Auto-truncates when value exceeds `maxLength` chars (default: 12).
 * Hover tooltip shows full value. Click copies to clipboard.
 *
 * Props:
 *   value      {string}  - the value to display
 *   maxLength  {number}  - chars before truncating (default: 12)
 *   monospace  {boolean} - force monospace font (default: true)
 *
 * Usage:
 *   import TruncCell from '../Common/components/TruncCell'
 *
 *   // In any column definition:
 *   render: (row) => <TruncCell value={row.id} />
 *   render: (row) => <TruncCell value={row.filePath} maxLength={20} />
 *   render: (row) => <TruncCell value={row.description} monospace={false} />
 */
function TruncCell({ value, maxLength = 12, monospace = true }) {
  const classes = useStyles()
  const theme = useTheme()
  const [copied, setCopied] = useState(false)

  if (value == null || value === '') {
    return <span style={{ color: theme.palette.text.disabled }}>—</span>
  }

  const str = String(value)
  const isLong = str.length > maxLength
  const display = isLong ? str.slice(0, maxLength) + '…' : str

  const handleCopy = (e) => {
    e.stopPropagation()
    if (navigator.clipboard) {
      navigator.clipboard.writeText(str).then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      })
    }
  }

  return (
    <Tooltip
      title={
        <Box>
          <Typography
            variant='caption'
            style={{ fontFamily: monospace ? 'monospace' : 'inherit', letterSpacing: '0.04em', wordBreak: 'break-all' }}
          >
            {str}
          </Typography>
          <Typography variant='caption' style={{ display: 'block', opacity: 0.7, marginTop: 2 }}>
            {copied ? '✓ Copied!' : 'Click to copy'}
          </Typography>
        </Box>
      }
      arrow
      placement='top'
    >
      <Box
        component='span'
        className={`${classes.pill} ${copied ? classes.pillCopied : ''}`}
        onClick={handleCopy}
      >
        <span
          className={classes.text}
          style={monospace ? undefined : { fontFamily: 'inherit' }}
        >
          {display}
        </span>
        <FontAwesomeIcon
          icon={['fal', copied ? 'check' : 'copy']}
          className={classes.icon}
          style={{ color: copied ? theme.palette.success.main : theme.palette.text.disabled }}
        />
      </Box>
    </Tooltip>
  )
}

export default TruncCell
